import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  Save,
  Settings as SettingsIcon,
  Printer,
  QrCode,
  Info,
  ShieldCheck,
  Store,
  Sparkles,
  Headphones,
  Phone,
  Key,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import CustomerSupportModal from "@/components/CustomerSupportModal";

export default function Settings() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);

  useEffect(() => {
    api
      .get("/settings")
      .then((r) => setS(r.data || {}))
      .catch(() => setS({}));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        shop_name_english: s?.shop_name_english ?? "",
        shop_name_marathi: s?.shop_name_marathi ?? "",
        brand_name: "PrintQ",
        tagline: "Scan. Enhance. Print.",
      };
      const { data } = await api.put("/settings", payload);
      setS(data);
      toast.success("Shop settings saved successfully!");
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  if (!s) return null;

  return (
    <div className="min-h-[calc(100vh-4rem)] p-4 md:p-6 max-w-4xl mx-auto flex flex-col gap-6 select-none" data-testid="settings-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 bg-zinc-900/70 border border-zinc-800 rounded-2xl p-5 backdrop-blur-md shadow-xl">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400">
              <SettingsIcon className="w-4 h-4" />
            </div>
            <h1 className="text-xl md:text-2xl font-bold tracking-tight text-white flex items-center gap-1.5">
              <span>Print</span>
              <span className="text-amber-400">Q</span>
              <span className="text-zinc-400 font-medium text-lg ml-1">Shop Settings</span>
            </h1>
          </div>
          <p className="text-xs md:text-sm text-zinc-400 mt-1">
            Configure shop profile, display name, and system preferences.
          </p>
        </div>

        <button
          onClick={save}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-400 to-amber-500 hover:brightness-110 text-zinc-950 text-xs font-bold shadow-md shadow-amber-500/20 transition-all active:scale-95"
        >
          <Save className="w-4 h-4" />
          {saving ? "Saving..." : "Save Settings"}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {/* Shop Info Form (7 Cols) */}
        <div className="md:col-span-7 bg-zinc-900/80 border border-zinc-800 rounded-2xl p-6 flex flex-col gap-4 shadow-lg">
          <h2 className="text-sm font-bold text-white flex items-center gap-2">
            <Store className="w-4 h-4 text-amber-400" />
            Shop Information
          </h2>

          <div className="space-y-4">
            <div>
              <label className="text-xs text-zinc-400 font-semibold mb-1.5 block">
                Shop Display Name (English)
              </label>
              <input
                className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-amber-500"
                value={s.shop_name_english || ""}
                onChange={(e) => setS({ ...s, shop_name_english: e.target.value })}
                placeholder="e.g. Jai Santoshi Mata Digital Center"
              />
            </div>

            <div>
              <label className="text-xs text-zinc-400 font-semibold mb-1.5 block">
                Shop Name (Local / Regional Language)
              </label>
              <input
                className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-amber-500"
                value={s.shop_name_marathi || ""}
                onChange={(e) => setS({ ...s, shop_name_marathi: e.target.value })}
                placeholder="e.g. जय संतोषी माँ डिजिटल सेवा केंद्र"
              />
            </div>
          </div>

          <div className="pt-2 border-t border-zinc-800/80 flex items-center justify-between text-xs">
            <span className="text-zinc-400">Operator Username:</span>
            <span className="font-mono text-amber-400 font-bold">{user?.username || "admin"}</span>
          </div>
        </div>

        {/* Quick Links & About PrintQ (5 Cols) */}
        <div className="md:col-span-5 flex flex-col gap-5">
          {/* Quick Shortcuts */}
          <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-5 flex flex-col gap-3 shadow-lg">
            <h3 className="text-sm font-bold text-white">Management Shortcuts</h3>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => nav("/printers")}
                className="w-full p-3 rounded-xl bg-zinc-950/70 border border-zinc-800 hover:border-amber-500/50 flex items-center justify-between text-xs text-zinc-200 transition-colors"
              >
                <div className="flex items-center gap-2.5">
                  <Printer className="w-4 h-4 text-emerald-400" />
                  <span>Configure Windows Printers</span>
                </div>
                <span className="text-zinc-500">→</span>
              </button>

              <button
                onClick={() => nav("/qr-setup")}
                className="w-full p-3 rounded-xl bg-zinc-950/70 border border-zinc-800 hover:border-amber-500/50 flex items-center justify-between text-xs text-zinc-200 transition-colors"
              >
                <div className="flex items-center gap-2.5">
                  <QrCode className="w-4 h-4 text-amber-400" />
                  <span>Customer QR & Rates</span>
                </div>
                <span className="text-zinc-500">→</span>
              </button>
            </div>
          </div>

          {/* Customer Support Card */}
          <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-5 flex flex-col gap-3 shadow-lg">
            <div className="flex items-center gap-2">
              <Headphones className="w-4 h-4 text-amber-400" />
              <h3 className="text-sm font-bold text-white">Customer Support</h3>
            </div>

            <div className="p-4 rounded-xl bg-zinc-950/70 border border-zinc-800/80 flex flex-col gap-3">
              <div>
                <div className="text-xs font-bold text-white">Kunal Warkante</div>
                <div className="text-[11px] text-zinc-400">Technical Support & Licensing</div>
              </div>

              <div className="flex items-center justify-between bg-zinc-900/90 border border-zinc-800 p-2.5 rounded-lg">
                <div className="flex items-center gap-2">
                  <Phone className="w-3.5 h-3.5 text-amber-400" />
                  <span className="font-mono text-xs font-bold text-white">+91 9545262332</span>
                </div>
                <button
                  type="button"
                  onClick={() => setShowSupportModal(true)}
                  className="px-2 py-0.5 rounded bg-amber-400 text-zinc-950 text-[10px] font-bold"
                >
                  Contact
                </button>
              </div>
            </div>
          </div>

          {/* About PrintQ Card */}
          <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-5 flex flex-col gap-3 shadow-lg">
            <div className="flex items-center gap-2">
              <Info className="w-4 h-4 text-amber-400" />
              <h3 className="text-sm font-bold text-white">About Software</h3>
            </div>

            <div className="p-4 rounded-xl bg-zinc-950/70 border border-zinc-800/80 flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-zinc-400">Product:</span>
                <span className="text-xs font-bold text-white">PrintQ</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-zinc-400">Tagline:</span>
                <span className="text-xs font-semibold text-amber-400">Scan. Enhance. Print.</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-zinc-400">Publisher:</span>
                <span className="text-xs font-semibold text-zinc-300">JSM DIGITAL SOLUTIONS</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-zinc-400">Version:</span>
                <span className="font-mono text-xs text-emerald-400 font-bold">v2.0 Commercial</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CustomerSupportModal
        isOpen={showSupportModal}
        onClose={() => setShowSupportModal(false)}
      />
    </div>
  );
}
